'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PlatformCard from './PlatformCard';




import SecurityCompliance from './SecurityCompliance';

interface Platform {
  id: string;
  name: string;
  tagline: string;
  description: string;
  image: string;
  imageAlt: string;
  features: {title: string;description: string;}[];
  primaryColor: string;
}

interface CaseStudy {
  company: string;
  industry: string;
  challenge: string;
  solution: string;
  metrics: {value: string;label: string;}[];
  platform: string;
}

interface MatrixFeature {
  category: string;
  features: {
    name: string;
    sferal: boolean;
    aidooAssist: boolean;
    aidooAnalyzer: boolean;
  }[];
}

interface TimelinePhase {
  phase: string;
  duration: string;
  activities: string[];
  deliverables: string[];
}

interface ComplianceItem {
  standard: string;
  description: string;
  icon: string;
}

const SolutionsInteractive = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');

  const platforms: Platform[] = [
  {
    id: 'sferal',
    name: 'Sferal.ai',
    tagline: 'Enterprise AI Orchestration Platform',
    description: 'Comprehensive AI governance and orchestration solution that enables organizations to deploy, monitor, and manage multiple AI models across the enterprise with complete visibility and control.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b9cfc75-1764651774193.png",
    imageAlt: 'Digital network visualization with glowing blue nodes and connections representing AI orchestration across global infrastructure',
    features: [
    {
      title: 'Multi-Model Orchestration',
      description: 'Deploy and manage multiple AI models from different providers in a unified platform'
    },
    {
      title: 'Real-Time Governance',
      description: 'Monitor AI decisions with complete audit trails and compliance reporting'
    },
    {
      title: 'Enterprise Integration',
      description: 'Seamless integration with existing enterprise systems and workflows'
    }],

    primaryColor: 'primary'
  },
  {
    id: 'aidoo-assist',
    name: 'AIDoo Assist',
    tagline: 'Intelligent Customer Service Platform',
    description: 'AI-powered customer service solution that combines natural language processing with human oversight to deliver exceptional customer experiences while maintaining brand consistency and compliance.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a63d8e65-1764642435785.png",
    imageAlt: 'Modern customer service representative wearing headset working at computer with multiple screens showing customer interaction dashboards',
    features: [
    {
      title: 'Conversational AI',
      description: 'Natural language understanding for complex customer inquiries'
    },
    {
      title: 'Human-in-the-Loop',
      description: 'Seamless escalation to human agents when needed'
    },
    {
      title: 'Omnichannel Support',
      description: 'Consistent experience across email, chat, phone, and social media'
    }],

    primaryColor: 'accent'
  },
  {
    id: 'aidoo-analyzer',
    name: 'AIDoo Analyzer',
    tagline: 'Business Intelligence & Analytics',
    description: 'Advanced analytics platform that transforms enterprise data into actionable insights using AI-powered analysis, predictive modeling, and automated reporting capabilities.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecd42800-1767033343981.png",
    imageAlt: 'Business analytics dashboard displaying colorful charts, graphs, and data visualizations on large monitor in modern office',
    features: [
    {
      title: 'Predictive Analytics',
      description: 'AI-driven forecasting and trend analysis for strategic planning'
    },
    {
      title: 'Automated Reporting',
      description: 'Generate comprehensive reports with natural language insights'
    },
    {
      title: 'Data Visualization',
      description: 'Interactive dashboards with real-time business metrics'
    }],

    primaryColor: 'secondary'
  }];


  const caseStudies: CaseStudy[] = [
  {
    company: 'Australian Financial Services Ltd',
    industry: 'Financial Services',
    challenge: 'Managing compliance across multiple AI systems while maintaining customer service quality and reducing operational costs.',
    solution: 'Implemented Sferal.ai to orchestrate customer service AI, fraud detection models, and risk assessment systems with unified governance framework.',
    metrics: [
    { value: '47%', label: 'Cost Reduction' },
    { value: '99.8%', label: 'Compliance Rate' },
    { value: '3.2x', label: 'Faster Processing' }],

    platform: 'Sferal.ai'
  },
  {
    company: 'RetailConnect NZ',
    industry: 'Retail & E-commerce',
    challenge: 'Handling 50,000+ monthly customer inquiries with inconsistent response quality and long resolution times.',
    solution: 'Deployed AIDoo Assist with custom training on product catalog and company policies, integrated with existing CRM system.',
    metrics: [
    { value: '68%', label: 'Query Automation' },
    { value: '4.8/5', label: 'Customer Satisfaction' },
    { value: '12min', label: 'Avg Response Time' }],

    platform: 'AIDoo Assist'
  },
  {
    company: 'HealthCare Analytics Group',
    industry: 'Healthcare',
    challenge: 'Analyzing patient data across multiple facilities to identify trends and optimize resource allocation while maintaining HIPAA compliance.',
    solution: 'Implemented AIDoo Analyzer with healthcare-specific models for predictive analytics and automated compliance reporting.',
    metrics: [
    { value: '34%', label: 'Resource Optimization' },
    { value: '89%', label: 'Prediction Accuracy' },
    { value: '100%', label: 'HIPAA Compliance' }],

    platform: 'AIDoo Analyzer'
  },
  {
    company: 'Manufacturing Solutions Australia',
    industry: 'Manufacturing',
    challenge: 'Coordinating AI-driven quality control, predictive maintenance, and supply chain optimization across 12 facilities.',
    solution: 'Deployed Sferal.ai to orchestrate multiple specialized AI models with centralized monitoring and governance.',
    metrics: [
    { value: '56%', label: 'Defect Reduction' },
    { value: '41%', label: 'Downtime Decrease' },
    { value: '$2.8M', label: 'Annual Savings' }],

    platform: 'Sferal.ai'
  },
  {
    company: 'TelcoServe NZ',
    industry: 'Telecommunications',
    challenge: 'Managing high-volume technical support inquiries with complex troubleshooting requirements and regulatory compliance.',
    solution: 'Implemented AIDoo Assist with technical knowledge base integration and escalation protocols for complex issues.',
    metrics: [
    { value: '72%', label: 'First-Contact Resolution' },
    { value: '45%', label: 'Support Cost Reduction' },
    { value: '4.6/5', label: 'CSAT Score' }],

    platform: 'AIDoo Assist'
  },
  {
    company: 'Energy Analytics Corp',
    industry: 'Energy & Utilities',
    challenge: 'Analyzing consumption patterns across 500,000+ customers to optimize grid management and predict demand fluctuations.',
    solution: 'Deployed AIDoo Analyzer with custom energy consumption models and real-time monitoring dashboards.',
    metrics: [
    { value: '28%', label: 'Grid Efficiency Gain' },
    { value: '92%', label: 'Demand Forecast Accuracy' },
    { value: '18%', label: 'Cost Savings' }],

    platform: 'AIDoo Analyzer'
  }];


  const comparisonFeatures: MatrixFeature[] = [
  {
    category: 'Core Capabilities',
    features: [
    { name: 'Multi-Model Orchestration', sferal: true, aidooAssist: false, aidooAnalyzer: false },
    { name: 'Natural Language Processing', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Predictive Analytics', sferal: true, aidooAssist: false, aidooAnalyzer: true },
    { name: 'Real-Time Monitoring', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Automated Reporting', sferal: true, aidooAssist: true, aidooAnalyzer: true }]

  },
  {
    category: 'Governance & Compliance',
    features: [
    { name: 'Audit Trail Management', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Role-Based Access Control', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Compliance Reporting', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Data Residency Options', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Policy Enforcement', sferal: true, aidooAssist: false, aidooAnalyzer: false }]

  },
  {
    category: 'Integration & Deployment',
    features: [
    { name: 'API Integration', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Cloud & On-Premise', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'CRM Integration', sferal: true, aidooAssist: true, aidooAnalyzer: false },
    { name: 'BI Tool Integration', sferal: false, aidooAssist: false, aidooAnalyzer: true },
    { name: 'Custom Workflows', sferal: true, aidooAssist: true, aidooAnalyzer: true }]

  },
  {
    category: 'User Experience',
    features: [
    { name: 'Intuitive Dashboard', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Mobile Access', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Customizable Interface', sferal: true, aidooAssist: true, aidooAnalyzer: true },
    { name: 'Multi-Language Support', sferal: true, aidooAssist: true, aidooAnalyzer: false },
    { name: 'White-Label Options', sferal: true, aidooAssist: true, aidooAnalyzer: false }]

  }];


  const timelinePhases: TimelinePhase[] = [
  {
    phase: 'Discovery & Assessment',
    duration: '2-3 weeks',
    activities: [
    'Current state analysis and requirements gathering',
    'Technical infrastructure assessment',
    'Stakeholder interviews and use case identification',
    'Risk assessment and compliance review'],

    deliverables: [
    'Detailed implementation roadmap',
    'Technical architecture document',
    'Risk mitigation strategy',
    'Success metrics framework']

  },
  {
    phase: 'Platform Configuration',
    duration: '3-4 weeks',
    activities: [
    'Environment setup and security configuration',
    'Integration with existing systems',
    'Custom workflow development',
    'Initial model training and optimization'],

    deliverables: [
    'Configured platform environment',
    'Integration documentation',
    'Custom workflow specifications',
    'Trained AI models']

  },
  {
    phase: 'Pilot Deployment',
    duration: '4-6 weeks',
    activities: [
    'Limited production deployment',
    'User acceptance testing',
    'Performance monitoring and optimization',
    'Feedback collection and refinement'],

    deliverables: [
    'Pilot deployment report',
    'Performance metrics dashboard',
    'User feedback analysis',
    'Optimization recommendations']

  },
  {
    phase: 'Full Production Rollout',
    duration: '2-3 weeks',
    activities: [
    'Enterprise-wide deployment',
    'User training and onboarding',
    'Change management support',
    'Go-live support and monitoring'],

    deliverables: [
    'Production environment',
    'Training materials and documentation',
    'Support procedures',
    'Performance baseline metrics']

  },
  {
    phase: 'Optimization & Scale',
    duration: 'Ongoing',
    activities: [
    'Continuous performance monitoring',
    'Model retraining and improvement',
    'Feature enhancement and expansion',
    'Regular governance reviews'],

    deliverables: [
    'Monthly performance reports',
    'Optimization recommendations',
    'Updated documentation',
    'Quarterly business reviews']

  }];


  const complianceItems: ComplianceItem[] = [
  {
    standard: 'ISO 27001',
    description: 'Information security management system certification ensuring robust data protection and security controls',
    icon: 'ShieldCheckIcon'
  },
  {
    standard: 'SOC 2 Type II',
    description: 'Independent audit of security, availability, and confidentiality controls',
    icon: 'DocumentCheckIcon'
  },
  {
    standard: 'GDPR Compliant',
    description: 'Full compliance with European data protection regulations for international operations',
    icon: 'GlobeAltIcon'
  },
  {
    standard: 'Australian Privacy Act',
    description: 'Adherence to Australian Privacy Principles for data handling and protection',
    icon: 'LockClosedIcon'
  },
  {
    standard: 'NZ Privacy Act 2020',
    description: 'Compliance with New Zealand privacy legislation and information privacy principles',
    icon: 'DocumentTextIcon'
  },
  {
    standard: 'APRA CPS 234',
    description: 'Information security standards for APRA-regulated entities in financial services',
    icon: 'BuildingLibraryIcon'
  }];


  const industries = ['all', 'Financial Services', 'Retail & E-commerce', 'Healthcare', 'Manufacturing', 'Telecommunications', 'Energy & Utilities'];

  const filteredCaseStudies = caseStudies.filter((study) => {
    const platformMatch = selectedPlatform === 'all' || study.platform === selectedPlatform;
    const industryMatch = selectedIndustry === 'all' || study.industry === selectedIndustry;
    return platformMatch && industryMatch;
  });

  const handleLearnMore = (platformId: string) => {
    const element = document.getElementById(`${platformId}-details`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-gradient-to-br from-secondary via-secondary to-primary text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white bg-opacity-10 px-4 py-2 rounded-full mb-6">
              <Icon name="CubeTransparentIcon" size={20} />
              <span className="text-sm font-medium">From SMB to Enterprise AI Solutions</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-display font-semibold mb-6">
              AI Orchestration Platforms Built for Enterprise Control
            </h1>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href="/partnership-inquiry"
                className="inline-flex items-center px-8 py-4 bg-accent text-accent-foreground font-display font-medium rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5 space-x-2">

                <span>Request Consultation</span>
                <Icon name="ArrowRightIcon" size={20} />
              </Link>
              <Link
                href="/ai-readiness-assessment"
                className="inline-flex items-center px-8 py-4 bg-white bg-opacity-10 text-white font-display font-medium rounded-lg hover:bg-opacity-20 transition-all duration-300 space-x-2 border border-white border-opacity-20">

                <span>Assess Your Readiness</span>
                <Icon name="ClipboardDocumentCheckIcon" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-4xl font-display font-semibold text-secondary mb-6">
            Three Platforms, One Unified Approach
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Unlike generic AI vendors offering disconnected tools, Mindshift AI provides an integrated ecosystem of platforms designed to work together, ensuring consistent governance, seamless data flow, and measurable business outcomes across your entire AI implementation.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          {platforms.map((platform) =>
          <PlatformCard
            key={platform.id}
            name={platform.name}
            tagline={platform.tagline}
            description={platform.description}
            image={platform.image}
            imageAlt={platform.imageAlt}
            features={platform.features}
            primaryColor={platform.primaryColor}
            onLearnMore={() => handleLearnMore(platform.id)} />

          )}
        </div>

        <div id="sferal-details" className="mb-20 scroll-mt-20">
          <SecurityCompliance platform="Sferal.ai" compliances={complianceItems} />
        </div>

        <div id="aidoo-assist-details" className="mb-20 scroll-mt-20">
          <SecurityCompliance platform="AIDoo Assist" compliances={complianceItems} />
        </div>

        <div id="aidoo-analyzer-details" className="mb-20 scroll-mt-20">
          <SecurityCompliance platform="AIDoo Analyzer" compliances={complianceItems} />
        </div>

        <div className="bg-gradient-to-br from-secondary via-primary to-accent text-white rounded-2xl p-12 mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-display font-semibold mb-6">
              Why Choose Mindshift AI Platforms?
            </h2>
            <p className="text-xl text-white text-opacity-90 mb-12 leading-relaxed">
              We're not just another AI vendor. We're your exclusive regional partner for governed, measurable AI transformation.
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="ShieldCheckIcon" size={24} />
                </div>
                <h3 className="text-xl font-display font-semibold mb-3">Complete Governance</h3>
                <p className="text-white text-opacity-90">
                  Every AI decision is traceable, auditable, and compliant with your organizational policies and regulatory requirements
                </p>
              </div>

              <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="ChartBarSquareIcon" size={24} />
                </div>
                <h3 className="text-xl font-display font-semibold mb-3">Measurable ROI</h3>
                <p className="text-white text-opacity-90">
                  Track concrete business outcomes with detailed analytics showing cost savings, efficiency gains, and performance improvements
                </p>
              </div>

              <div className="bg-white bg-opacity-10 rounded-xl p-6 backdrop-blur-sm">
                <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="UserGroupIcon" size={24} />
                </div>
                <h3 className="text-xl font-display font-semibold mb-3">Local Expertise</h3>
                <p className="text-white text-opacity-90">
                  Deep understanding of Australian and New Zealand regulatory environments, business culture, and market conditions
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl shadow-authority p-12 border border-border">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-display font-semibold text-secondary mb-6">
              Ready to Transform Your AI Strategy?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Schedule a consultation with our AI governance experts to discuss your specific requirements and explore how our platforms can deliver measurable business outcomes for your organization.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                href="/partnership-inquiry"
                className="inline-flex items-center px-8 py-4 bg-accent text-accent-foreground font-display font-medium rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5 space-x-2">

                <span>Request Strategic Consultation</span>
                <Icon name="ArrowRightIcon" size={20} />
              </Link>
              <Link
                href="/ai-readiness-assessment"
                className="inline-flex items-center px-8 py-4 bg-secondary text-secondary-foreground font-display font-medium rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5 space-x-2">

                <span>Take AI Readiness Assessment</span>
                <Icon name="ClipboardDocumentCheckIcon" size={20} />
              </Link>
            </div>

            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4">
                Trusted by leading enterprises across Australia and New Zealand
              </p>
              <div className="flex flex-wrap justify-center gap-8 items-center opacity-60">
                <div className="text-2xl font-display font-semibold text-secondary">Financial Services</div>
                <div className="text-2xl font-display font-semibold text-secondary">Healthcare</div>
                <div className="text-2xl font-display font-semibold text-secondary">Manufacturing</div>
                <div className="text-2xl font-display font-semibold text-secondary">Retail</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-secondary text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <span className="text-xl font-display font-semibold">Mindshift AI</span>
              </div>
              <p className="text-sm text-white text-opacity-80">
                Your exclusive regional partner for governed AI transformation
              </p>
            </div>

            <div>
              <h4 className="font-display font-semibold mb-4">Solutions</h4>
              <ul className="space-y-2 text-sm text-white text-opacity-80">
                <li>
                  <Link href="/solutions-overview" className="hover:text-accent transition-colors duration-200">
                    Sferal.ai
                  </Link>
                </li>
                <li>
                  <Link href="/solutions-overview" className="hover:text-accent transition-colors duration-200">
                    AIDoo Assist
                  </Link>
                </li>
                <li>
                  <Link href="/solutions-overview" className="hover:text-accent transition-colors duration-200">
                    AIDoo Analyzer
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-display font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-white text-opacity-80">
                <li>
                  <Link href="/results-and-roi" className="hover:text-accent transition-colors duration-200">
                    Case Studies
                  </Link>
                </li>
                <li>
                  <Link href="/ai-readiness-assessment" className="hover:text-accent transition-colors duration-200">
                    AI Readiness Assessment
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-display font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-white text-opacity-80">
                <li>
                  <Link href="/partnership-inquiry" className="hover:text-accent transition-colors duration-200">
                    Request Consultation
                  </Link>
                </li>
                <li>New Zealand: +64 21 271 0808</li>
                <li>info@mindshiftai.co.nz</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white border-opacity-20 pt-8 text-center text-sm text-white text-opacity-60">
            <p>&copy; 2026 Mindshift AI. All rights reserved. | Privacy Policy | Terms of Service</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default SolutionsInteractive;